import type { NavGroup, Section } from "./facility.types";

// ── Facility navigation (HU-062.1) ──────────────────────────────
// The facility cockpit: 7 operational sections + 3 support entries. The 12
// descriptive book-clone sections were retired from the nav; their content
// lives in the book (longitudinal) or as drill-downs inside Collateral/Portfolio.

export const FACILITY_NAV: NavGroup = {
  label: "Facility",
  items: [
    { id: "overview", label: "Overview", ready: true },
    { id: "collateral", label: "Collateral", ready: true },
    { id: "covenants", label: "Covenants", ready: true },
    { id: "cash", label: "Cash & Reconciliation", ready: true },
    // The liability side (line drawn + notes). Both the label and the id say
    // obligation: the cockpit around it is the facility itself, so the old
    // name meant the container and its content at once. Old ?section=facility
    // links land here through SECTION_REDIRECTS.
    { id: "obligation", label: "Obligation", ready: true },
    { id: "waterfall", label: "Waterfall", ready: true },
    { id: "incidents", label: "Incidents", ready: true },
    { id: "config", label: "Config", ready: true },
    { id: "dataroom", label: "Data Room", ready: true },
    { id: "upload", label: "Upload Files", ready: true },
  ],
};

// ── Non-admin facility nav ──────────────────────────────────────
// What a deal_view / deals_view grant opens: the whole reading of the facility.
// Both grants name a DEAL — the contract — and reach every facility under it,
// this one included; `canViewDeal(perms, facility.deal_id)` is how FacilityPage
// asks. Only the three surfaces that are not a reading of the facility are
// absent:
//   · Config — the facility's configuration, Debita's to write
//   · Data Room / Upload Files — book-scoped endpoints; the originator uploads
//     and reads its files from its book, which is where those permissions live
// Sections that DO carry Debita's own work — Cash & Reconciliation, Incidents —
// are here as readings: the counterparty sees the book and the incident record,
// never the buttons that act on them. Hiding the write surface is the section's
// own job, through its `canManage` prop.
//
// This list is the surface, not the enforcement: FacilityPage blocks the same
// ids by URL through `restrictedSections`, and every endpoint carries its own guard.
const NON_ADMIN_SECTIONS = new Set<string>([
  "overview",
  "collateral",
  "covenants",
  "cash",
  "obligation",
  "waterfall",
  "incidents",
]);

export const NON_ADMIN_FACILITY_NAV: NavGroup = {
  label: "Facility",
  items: FACILITY_NAV.items.filter((i) => NON_ADMIN_SECTIONS.has(i.id)),
};

// ── Anonymous guest nav (HU-066 §4) ─────────────────────────
// The analytical subset. Two of the seven come off for a guest reading under an
// anonymous grant, and for the same reason in both cases — the payload names
// the originator and has no anonymous form, so the server answers 403 rather
// than serving a hollowed-out record:
//   · Cash & Reconciliation — a statement line carries the account holder in
//     its description, its counterparty and its reference
//   · Incidents — the evidence items are file names and account references
const ANON_SECTIONS = new Set<string>(["cash", "incidents"]);

export const ANON_FACILITY_NAV: NavGroup = {
  label: "Facility",
  items: NON_ADMIN_FACILITY_NAV.items.filter((i) => !ANON_SECTIONS.has(i.id)),
};

/** Sections a non-admin never reaches, by crafted URL either — the mirror of
 *  the two navs above, so one decision is not spelled out twice by hand. */
export function restrictedSections(anonymous: boolean): Set<string> {
  const allowed = anonymous ? ANON_FACILITY_NAV.items : NON_ADMIN_FACILITY_NAV.items;
  const ids = new Set(allowed.map((i) => i.id));
  return new Set(FACILITY_NAV.items.map((i) => i.id).filter((id) => !ids.has(id)));
}

export const SECTION_TITLES: Record<Section, string> = {
  overview: "Overview",
  collateral: "Collateral",
  covenants: "Covenants",
  cash: "Cash & Reconciliation",
  obligation: "Obligation",
  waterfall: "Waterfall",
  incidents: "Incidents",
  config: "Config",
  dataroom: "Data Room",
  upload: "Upload Files",
};

// ── Legacy ?section= redirects (HU-062.1, criterion 2) ──────
// Every old section id maps to its new home so bookmarked/linked URLs keep
// working. Derived from the target architecture table in the HU:
//  , Overview absorbs the facility landing + KPIs + FX/hedge
//  , Collateral absorbs the BB cascade, concentration and pledged-pool views
//  , Cash & Reconciliation is the fusion of bank statements + matching
//  , Portfolio-lite absorbs the descriptive pool views (also live in the book)
export const SECTION_REDIRECTS: Record<string, Section> = {
  key_indicators: "overview",
  fx_risk: "overview",
  borrowing_base: "collateral",
  concentration: "collateral",
  characteristics: "collateral",
  portfolio_concentration: "collateral",
  facility_delinquency: "collateral",
  reconciliation: "cash",
  bank_statement: "cash",
  // The obligation section was `facility` until the id said what the section is
  // rather than what contains it; every bookmarked ?section=facility lands here.
  facility: "obligation",
  // The Portfolio section folded into Collateral (facility review P7): its DPD
  // trending + stratification live in the Delinquency tab, new-vs-repeat in
  // Acquisition. Deep longitudinal analytics stay on the originator book.
  portfolio: "collateral",
  traction: "collateral",
  delinquency: "collateral",
  collection: "collateral",
  curves: "collateral",
  cohort: "collateral",
  yields: "collateral",
  cashflow: "collateral",
  performance: "collateral",
  data_room: "dataroom",
};

// ── Facility shape ──────────────────────────────────────────
// Which terms a facility is read against — note balance/rate/close date, or line
// size/advance rate/first draw — depends on whether it is a securitization.
//
// This MUST be an allowlist of securitization types, never `!== "WAREHOUSE"`.
// The backend column is a free-form string with no validator (models/facility.py
// declares FACILITY_TYPES but nothing enforces it), so unknown values do reach
// the frontend: AVG_I is stored as "REVOLVING_WAREHOUSE", which a negated
// equality classified as a securitization and rendered with note fields on a
// warehouse line. Anything unrecognised falls back to the model's own default
// (WAREHOUSE), which is the conservative read.
const SECURITIZATION_TYPES = new Set([
  "TERM_SECURITIZATION",
  "REVOLVING_SECURITIZATION",
  "SECURITIZATION",
]);

export function isSecuritization(facilityType: string | null | undefined): boolean {
  return SECURITIZATION_TYPES.has((facilityType ?? "").toUpperCase());
}

const CANONICAL_SECTIONS = new Set<string>(FACILITY_NAV.items.map((i) => i.id));

/**
 * Resolve any ?section= value to a canonical facility section (HU-062.1).
 * Canonical ids pass through; legacy ids redirect via SECTION_REDIRECTS;
 * unknown/empty falls back to the overview landing.
 */
export function resolveSection(raw: string | null | undefined): Section {
  if (raw && CANONICAL_SECTIONS.has(raw)) return raw as Section;
  if (raw && raw in SECTION_REDIRECTS) return SECTION_REDIRECTS[raw];
  return "overview";
}

export const PAR_COLORS: Record<string, string> = {
  current: "#059669",
  par_1_30: "#D97706",
  par_31_60: "#EA580C",
  par_61_90: "#DC2626",
  par_90_plus: "#7F1D1D",
};

export const PAR_LABELS: Record<string, string> = {
  current: "Current",
  par_1_30: "PAR 1-30",
  par_31_60: "PAR 31-60",
  par_61_90: "PAR 61-90",
  par_90_plus: "PAR 90+",
};

export const DPD_COLORS: Record<string, string> = {
  CURRENT: "#059669",
  "1-30": "#D97706",
  "31-60": "#EA580C",
  "61-90": "#DC2626",
  "91-120": "#B91C1C",
  "121-180": "#991B1B",
  "181-360": "#7F1D1D",
  "360+": "#450A0A",
};

export const COHORT_BUCKET_COLORS: Record<string, string> = {
  CURRENT: "#059669",
  "1-30": "#D97706",
  "31-60": "#EA580C",
  "61-90": "#DC2626",
  "90+": "#7F1D1D",
  REPAID: "#6B7280",
  // A live loan whose tape reported no DPD: its own bucket, never folded into
  // CURRENT (nobody said it is up to date) nor into 90+ (the SQL ladder's ELSE).
  UNREPORTED: "#9CA3AF",
};

export const COHORT_BUCKETS = ["CURRENT", "1-30", "31-60", "61-90", "90+", "UNREPORTED", "REPAID"] as const;

const DPD_ORDER = ["CURRENT", "1-30", "31-60", "61-90", "91-120", "121-180", "181-360", "360+"];
const TENOR_ORDER = ["≤6m", "7-12m", "13-18m", "19-24m", "25-30m", "31-36m", "37-42m", "43-48m", "49-60m", "61-72m", "72m+"];
const AMOUNT_BUCKET_ORDER = ["0-3M", "3-5M", "5-7M", "7-10M", "10M+"];

export function sortDPD<T extends { bucket: string }>(buckets: T[]): T[] {
  return [...buckets].sort((a, b) => DPD_ORDER.indexOf(a.bucket) - DPD_ORDER.indexOf(b.bucket));
}

export function tenorOrder(bucket: string): number {
  return TENOR_ORDER.indexOf(bucket);
}

export function bucketOrder(bucket: string): number {
  return AMOUNT_BUCKET_ORDER.indexOf(bucket);
}

// `wac` is already an annual decimal — the backend annualizes interest_rate at
// ingest, so this is a passthrough. Do NOT × 12 here (it used to be done in the
// cards, producing a 12× overstated rate).
export function getWacAnnual(ki: { wac?: number | null } | null | undefined): number | null {
  return ki?.wac ?? null;
}
